import { useEffect, useEffectEvent, useRef, useState } from 'react';
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  COPY_COMMAND,
  CUT_COMMAND,
  KEY_ENTER_COMMAND,
  TextNode,
} from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { AtIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { storedFormula } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import { loadExternalValue } from '@/lib/lexicalExternalValue';
import { cn } from '@/lib/utils';
import {
  $chipTypedReference,
  $copiedFormulaText,
  $formulaText,
  $isPickerTriggered,
  $setFormula,
} from './formulaEditorText';
import {
  FormulaFieldsContext,
  FormulaReferenceNode,
  FormulaTableReferenceNode,
} from './formulaReferenceNode';
import { FormulaReferencePicker } from './FormulaReferencePicker';
import type { FormulaFields } from './worksheetFormulaFields';

const SYNC_TAG = 'worksheet-formula-sync';

/**
 * Keeps the editor and the stored Formula in step: chips typed or pasted
 * references, reports each edit in stored form (holding back a trigger the
 * picker is open for, so a half-typed `@` is never saved), and reloads when the
 * value changes elsewhere.
 */
function FormulaSyncPlugin({
  value,
  fields,
  picking,
  onChange,
}: {
  value: string;
  fields: FormulaFields;
  picking: boolean;
  onChange: (expression: string) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const reported = useRef(value);
  const report = useEffectEvent((dismissed: boolean) => {
    const { stored, triggered } = editor.getEditorState().read(() => ({
      stored: storedFormula(
        $formulaText(),
        fields.inputName,
        fields.tables ?? [],
      ),
      triggered: $isPickerTriggered(),
    }));
    // A trigger just typed opens the picker: what it inserts is the edit,
    // unless the picker is dismissed and the trigger stays as typed.
    if ((triggered && !dismissed) || stored === reported.current) return;
    reported.current = stored;
    onChange(stored);
  });
  const chip = useEffectEvent((node: TextNode) =>
    $chipTypedReference(node, fields),
  );
  useEffect(
    () => editor.registerNodeTransform(TextNode, (node) => chip(node)),
    [editor],
  );
  const load = useEffectEvent(() => $setFormula(value, fields));
  useEffect(() => {
    if (value === reported.current) return;
    reported.current = value;
    loadExternalValue(editor, load, SYNC_TAG);
  }, [editor, value]);
  useEffect(
    () =>
      editor.registerUpdateListener(({ tags, dirtyLeaves, dirtyElements }) => {
        if (tags.has(SYNC_TAG) || (!dirtyLeaves.size && !dirtyElements.size))
          return;
        report(false);
      }),
    [editor],
  );
  const wasPicking = useRef(false);
  useEffect(() => {
    if (wasPicking.current && !picking) report(true);
    wasPicking.current = picking;
  }, [picking]);
  return null;
}

/**
 * Copy and cut put the text a spreadsheet reads on the clipboard: a Table
 * reference by its table's current name, not the id the editor holds.
 */
function ClipboardPlugin({ fields }: { fields: FormulaFields }) {
  const [editor] = useLexicalComposerContext();
  const copied = useEffectEvent(() => $copiedFormulaText(fields));
  useEffect(() => {
    const write =
      (cut: boolean) => (event: ClipboardEvent | KeyboardEvent | null) => {
        if (!event || !('clipboardData' in event) || !event.clipboardData)
          return false;
        const text = copied();
        if (text === null) return false;
        event.clipboardData.setData('text/plain', text);
        event.preventDefault();
        if (cut) {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) selection.removeText();
        }
        return true;
      };
    const unregister = [
      editor.registerCommand(COPY_COMMAND, write(false), COMMAND_PRIORITY_LOW),
      editor.registerCommand(CUT_COMMAND, write(true), COMMAND_PRIORITY_LOW),
    ];
    return () => unregister.forEach((remove) => remove());
  }, [editor]);
  return null;
}

/** A Formula is one line: Enter never adds a paragraph. */
function SingleLinePlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(
    () =>
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event) => {
          event?.preventDefault();
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    [editor],
  );
  return null;
}

function EditablePlugin({ readOnly }: { readOnly: boolean }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => editor.setEditable(!readOnly), [editor, readOnly]);
  return null;
}

/** Types `@` at the caret, which opens the picker. */
function InsertReferenceButton({ disabled }: { disabled: boolean }) {
  const gt = useGT();
  const [editor] = useLexicalComposerContext();
  const label = gt('Insert column reference', {
    context: 'worksheet insert reference',
  });
  return (
    <Button
      size="sm"
      variant="ghost"
      aria-label={label}
      title={label}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => {
        editor.focus();
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) selection.insertText('@');
          else $getRoot().selectEnd().insertText('@');
        });
      }}
    >
      <AtIcon />
    </Button>
  );
}

/**
 * The Formula editor. Every reference renders as a chip showing where its Field
 * comes from; `@`, `[` or `[@` opens a picker of the Fields; Backspace removes a
 * chip whole; copy yields the Excel text (`[@Cases] * [@[Case Price]]`) and
 * pasted text becomes chips. It reports the stored form.
 */
export function WorksheetFormulaInput({
  value,
  fields,
  onChange,
  label,
  optional = false,
  invalid = false,
  readOnly = false,
}: {
  value: string;
  fields: FormulaFields;
  onChange: (expression: string) => void;
  label: string;
  optional?: boolean;
  /** The stored Formula has a Diagnostic. */
  invalid?: boolean;
  readOnly?: boolean;
}) {
  const gt = useGT();
  const [picking, setPicking] = useState(false);
  const placeholder = gt('Type @ to reference a column', {
    context: 'worksheet formula placeholder',
  });
  return (
    <FormulaFieldsContext.Provider value={fields}>
      <LexicalComposer
        initialConfig={{
          namespace: 'worksheet-formula',
          nodes: [FormulaReferenceNode, FormulaTableReferenceNode],
          editable: !readOnly,
          editorState: () => {
            if (!(optional && !value)) $setFormula(value, fields);
          },
          onError: (error) => {
            throw error;
          },
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <div
            className={cn(
              'relative flex h-8 min-w-0 flex-1 items-center gap-1 rounded-md border bg-background px-2 font-mono text-sm focus-within:ring-2 focus-within:ring-ring/50',
              invalid && 'border-destructive',
            )}
          >
            <span aria-hidden className="text-muted-foreground">
              =
            </span>
            <PlainTextPlugin
              contentEditable={
                <ContentEditable
                  role="combobox"
                  ariaLabel={label}
                  ariaExpanded={picking}
                  ariaAutoComplete="list"
                  aria-haspopup="listbox"
                  ariaInvalid={invalid || undefined}
                  aria-placeholder={placeholder}
                  placeholder={
                    <span className="pointer-events-none absolute left-6 truncate text-muted-foreground">
                      {placeholder}
                    </span>
                  }
                  className="min-w-0 flex-1 overflow-x-auto whitespace-pre outline-none [scrollbar-width:none]"
                />
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <InsertReferenceButton disabled={readOnly} />
        </div>
        <HistoryPlugin />
        <SingleLinePlugin />
        <ClipboardPlugin fields={fields} />
        <EditablePlugin readOnly={readOnly} />
        <FormulaReferencePicker fields={fields} onOpenChange={setPicking} />
        <FormulaSyncPlugin
          value={value}
          fields={fields}
          picking={picking}
          onChange={onChange}
        />
      </LexicalComposer>
    </FormulaFieldsContext.Provider>
  );
}
